import { ReactNode, useEffect, useId, useRef } from 'react';

/**
 * Modal — dialog overlay. Native HTML <dialog> element for built-in focus trap +
 * Escape-to-close + modal backdrop.
 *
 * Derived from M3 Dialog (Basic + Full-Screen variants) per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/dialogs/overview
 *   - Reference: github.com/material-components/material-web/tree/main/dialog
 *
 * Populus adaptations applied:
 *   - Native <dialog> (modern browser support; Chrome 37+, Firefox 98+, Safari 15.4+).
 *     M3 reference impl uses a custom Lit element; native gives focus trap +
 *     Escape-key handling + backdrop click semantics for free.
 *   - Size variants: sm (confirm dialogs; max-width 400px) + lg (form dialogs;
 *     max-width 720px) + full (full-screen on mobile per M3 Full-Screen Dialog spec)
 *   - Backdrop click closes (M3 default + Populus convention)
 *   - Footer slot for action buttons (right-aligned per M3 Dialog spec)
 *
 * Token discipline:
 *   - Modal surface → --color-card (system)
 *   - Border → --color-border (system)
 *   - Shadow → --sh-modal (system; M3 elevation level 5)
 *   - Action buttons inside render their own brand-primary via Button atom
 *
 * Phase 11 Block 6 PR 3c (2026-05-12).
 */

export type ModalSize = 'sm' | 'lg' | 'full';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  closeOnBackdropClick?: boolean;
}

const sizeStyles: Record<ModalSize, React.CSSProperties> = {
  sm: { maxWidth: 400, width: '90vw' },
  lg: { maxWidth: 720, width: '90vw' },
  full: { maxWidth: '95vw', width: '95vw', height: '90vh' },
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'lg',
  closeOnBackdropClick = true,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  // The control that had focus when the dialog opened. Focus goes back to it on
  // close so a keyboard user carries on from where they were, rather than being
  // dropped at the top of the page. Most callers unmount the Modal on close
  // (`{open && <Modal …/>}`), which skips the native <dialog>'s own restore, so
  // this also runs from the unmount cleanup.
  const openerRef = useRef<HTMLElement | null>(null);

  // Sync open prop with native <dialog> imperative API
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      const active = document.activeElement;
      openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
      restoreFocus();
    }
  }, [open]);

  // Unmounted while open. The check on `isConnected` tells a real unmount (the
  // dialog has already left the DOM) from React StrictMode's rehearsal unmount in
  // development (the dialog is still there and about to be re-used).
  useEffect(() => {
    const dialog = dialogRef.current;
    return () => {
      if (!dialog?.isConnected) restoreFocus();
    };
  }, []);

  function restoreFocus() {
    const opener = openerRef.current;
    openerRef.current = null;
    if (opener && opener.isConnected) opener.focus();
  }

  // Bridge native <dialog>'s close event → React onClose
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => {
      restoreFocus();
      onClose();
    };
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  // Backdrop click — native <dialog> doesn't differentiate backdrop from
  // dialog body without manual hit-testing on the dialog element itself
  const handleClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (!closeOnBackdropClick) return;
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  return (
    // The dialog's click handler only detects a backdrop click; the keyboard
    // equivalent is Escape, which the native <dialog> already handles.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialogRef}
      aria-labelledby={title ? titleId : undefined}
      onClick={handleClick}
      style={{
        ...sizeStyles[size],
        // Center the dialog over the viewport. A modal <dialog> is placed in the
        // top layer at position:fixed with the UA `inset:0`, so `margin:auto`
        // centers it on both axes. This inline `margin` is required because the
        // global reset (`* { margin: 0 }`, styles/reset.css) is AUTHOR CSS that
        // beats the UA `dialog:modal { margin:auto }` — without re-supplying it,
        // inset:0 + margin:0 collapses the dialog to the top-left corner.
        // `inset:0` is set explicitly so centering never depends on the UA, and
        // `maxHeight:90vh` keeps a tall dialog on-screen (its body already scrolls).
        margin: 'auto',
        inset: 0,
        maxHeight: '90vh',
        padding: 0,
        border: 'none',
        borderRadius: 'var(--r-2xl)',
        background: 'var(--color-card)',
        color: 'var(--color-ink)',
        boxShadow: 'var(--sh-modal)',
        overflow: 'hidden',
      }}
    >
      {title && (
        <header style={{
          padding: 'var(--sp-4) var(--sp-5)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <h2 id={titleId} style={{
            margin: 0,
            fontSize: 'var(--fs-xl)',
            fontWeight: 'var(--fw-semibold)',
            color: 'var(--color-ink)',
          }}>{title}</h2>
        </header>
      )}
      <div style={{
        padding: 'var(--sp-5)',
        // A pop-up must never scroll horizontally: the shell is width-capped, so
        // scope overflow to the vertical axis only. Any genuinely wide child (a
        // table) brings its own overflow-x container.
        overflowX: 'hidden',
        overflowY: 'auto',
        maxHeight: size === 'full' ? 'calc(90vh - 140px)' : '70vh',
      }}>
        {children}
      </div>
      {footer && (
        <footer style={{
          padding: 'var(--sp-3) var(--sp-5)',
          borderTop: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--sp-2)',
        }}>
          {footer}
        </footer>
      )}
    </dialog>
  );
}
